import { Component, Prop, h, Host, Element, Event, EventEmitter, Method, State } from '@stencil/core';
import { AlertColor } from './alert-interface';
import { hasSlot } from '../../utils';

/**
 * @since 1.0
 * @status Stable
 *
 * @slot - Main content of the alert.
 * @slot icon - Show icon inside top left corner.
 * @slot heading - Set heading html content.
 * @slot primary - Place for primary button.
 * @slot secondary - Place for secondary button.
 *
 * @part alert - The component's main alert box.
 * @part heading - The heading element.
 * @part content - The content section.
 * @part actions - Section with button actions.
 */
@Component({
  tag: 'o2-alert',
  styleUrl: 'alert.scss',
  shadow: true,
})
export class O2Alert {
  private hasHeadingSlot: boolean;
  private hasIconSlot: boolean;
  private hasPrimaryActionSlot: boolean;
  private hasSecondaryActionSlot: boolean;
  private hasInlineActionSlot: boolean;
  private hasContent: boolean;

  @Element() private el!: HTMLO2AlertElement;

  @State() hidden: boolean = false;

  /**
   * Background color of alert.
   * Possible values: `"primary"`, `"success"`, `"warning"`, `"danger"`, `"purple"`.
   */
  @Prop() color?: AlertColor = 'primary';

  /**
   * If the value of the attribute is true, close button in the top right corner is visible.
   */
  @Prop({ reflect: true }) closable? = false;

  /**
   * Hide action is prevented, so custom code before close can be defined in o2Hide event. After that it can be hidden manually with hide() method.
   */
  @Prop() preventClose? = false;

  /**
   * Main title of the alert.
   */
  @Prop() heading?: string;

  /**
   * Set alert to inline type.
   */
  @Prop() inline?: boolean = false;

  /**
   * Emitted when the alert is hidden.
   */
  @Event() o2Hide!: EventEmitter<any>;

  /**
   * Hide alert manualy.
   */
  @Method()
  async hide() {
    this.hidden = true;
  }

  private handleCloseClick() {
    if (!this.preventClose) {
      this.hidden = true;
    }

    this.o2Hide.emit();
  }

  connectedCallback() {
    this.hasHeadingSlot = hasSlot(this.el, '[slot="heading"]');
    this.hasIconSlot = hasSlot(this.el, '[slot="icon"]');

    this.hasPrimaryActionSlot = hasSlot(this.el, '[slot="primary"]');
    this.hasSecondaryActionSlot = hasSlot(this.el, '[slot="secondary"]');
    this.hasInlineActionSlot = hasSlot(this.el, '[slot="inline"]');

    const hasInlineSlot = hasSlot(this.el, '[slot="inline"]');

    if (hasInlineSlot) {
      console.warn('[o2-alert] for actions use primary or primary with secondary slot.');
    }
  }

  componentWillLoad ():void {
    this.hasContent = this.el.innerHTML !== ''
  }

  render() {
    const hasHeading = !!this.heading || this.hasHeadingSlot;
    const hasAction = this.hasPrimaryActionSlot || this.hasSecondaryActionSlot || this.hasInlineActionSlot;

    return (
      <Host>
        <div
          role="alert"
          part="alert"
          class={`
            alert
            alert--${this.color}
            ${this.closable ? 'alert--closable' : ''}
            ${this.inline ? 'alert--inline' : ''}
            ${this.hidden ? 'alert--hidden' : ''}`
          }
        >
          {this.hasIconSlot && <slot name="icon" />}
          <div class="alert__section">
            {hasHeading && (
              <div class="alert__heading" part="heading">
                {this.heading ? this.heading : ''}
                {this.hasHeadingSlot && <slot name="heading" />}
              </div>
            )}
            {this.hasContent && (
              <div class="alert__content" part="content">
              <slot></slot>
            </div>
            )}
            {hasAction && (
              <div class="alert__actions" part="actions">
                {/* inline could be deprecated */}
                {this.hasInlineActionSlot && <slot name="inline" />}
                {this.hasPrimaryActionSlot && <slot name="primary" />}
                {this.hasSecondaryActionSlot && <slot name="secondary" />}
              </div>
            )}
          </div>
          {this.closable &&
            <o2-icon class="alert__close" name="close" size={16} onClick={() => this.handleCloseClick()}></o2-icon>
          }
        </div>
      </Host>
    );
  }
}
